/**
 * Owner: Masa Bayu
 * File: qx-draft-image-html.ts
 * Date:Time: 2026-10-16 01:10
 * Description: Figure block → draft HTML (size · float · picture · note).
 */

import { htmlDraftFigureNote } from './qx-draft-image-note';
import {
  draftFigureStyleClassNames,
  draftFigureStyleDataAttrs,
  type DraftFigurePicture,
} from './qx-draft-image-style';

type DraftFigureFloat = 'left' | 'right' | 'none';

function escapeHtmlAttr(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Figure block → draft HTML (size + float + picture style + note). */
export function htmlFromDraftFigure(input: {
  readonly assetId: string;
  readonly alt: string;
  readonly src?: string | undefined;
  readonly widthPx?: number | undefined;
  readonly heightPx?: number | undefined;
  readonly float?: DraftFigureFloat | undefined;
  readonly picture?: DraftFigurePicture | undefined;
  readonly note?: string | undefined;
}): string {
  const id = escapeHtmlAttr(input.assetId);
  const alt = escapeHtmlAttr(input.alt || 'Image');
  const float = input.float ?? 'left';
  const picture = input.picture ?? { frame: 'none', shadow: 'none' };
  const floatClass =
    float === 'right'
      ? 'qxw-draft-figure--float-right'
      : float === 'none'
        ? 'qxw-draft-figure--float-none'
        : 'qxw-draft-figure--float-left';
  const styleClass = draftFigureStyleClassNames(picture);
  const styleAttrs = draftFigureStyleDataAttrs(picture);
  const styles: string[] = [];
  if (input.widthPx !== undefined && input.widthPx > 0) styles.push(`width:${input.widthPx}px`);
  if (input.heightPx !== undefined && input.heightPx > 0) styles.push(`height:${input.heightPx}px`);
  else if (input.widthPx !== undefined && input.widthPx > 0) styles.push('height:auto');
  const sizeAttr =
    `${input.widthPx !== undefined && input.widthPx > 0 ? ` data-qx-width="${input.widthPx}"` : ''}` +
    `${input.heightPx !== undefined && input.heightPx > 0 ? ` data-qx-height="${input.heightPx}"` : ''}` +
    `${styles.length > 0 ? ` style="${styles.join(';')}"` : ''}`;
  const noteHtml = htmlDraftFigureNote(input.note);
  const figureOpen =
    `<figure class="qxw-draft-figure ${floatClass} ${styleClass}" contenteditable="false" draggable="false" data-qx-float="${float}"${styleAttrs}>`;
  if (input.src) {
    return `${figureOpen}<img data-qx-asset-id="${id}" alt="${alt}" src="${escapeHtmlAttr(input.src)}"${sizeAttr}>${noteHtml}</figure>`;
  }
  return `${figureOpen}<span class="qxw-fig-ph">${alt}</span>${noteHtml}</figure>`;
}
